import React, { useState, useEffect } from "react";
import Header from "../Header";
import Footer from "../Footer";
import API_BASE_URL from "../../Config";
import { useNavigate } from "react-router-dom";
import SunEditor from "suneditor-react";
import "suneditor/dist/css/suneditor.min.css";
import Select from "react-select";

/* ────────── SVG Icon System ────────── */
const Icon = ({ d, size = 18, stroke = 1.8 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={stroke}
    strokeLinecap="round"
    strokeLinejoin="round"
    style={{ display: "block", flexShrink: 0 }}
  >
    {d}
  </svg>
);

const UserIcon = (p) => (
  <Icon {...p} d={<><circle cx="12" cy="8" r="4" /><path d="M4 20c0-4 3.6-7 8-7s8 3 8 7" /></>} />
);
const CheckCircleIcon = (p) => (
  <Icon {...p} d={<><circle cx="12" cy="12" r="10" /><path d="m9 12 2 2 4-4" /></>} />
);
const XCircleIcon = (p) => (
  <Icon {...p} d={<><circle cx="12" cy="12" r="10" /><path d="m15 9-6 6M9 9l6 6" /></>} />
);
const CloseIcon = (p) => (
  <Icon {...p} d={<><path d="M18 6 6 18M6 6l12 12" /></>} />
);
const ChevronLeftIcon = (p) => (
  <Icon {...p} d={<><path d="m15 18-6-6 6-6" /></>} />
);
const AlertTriangleIcon = (p) => (
  <Icon {...p} d={<><path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3Z" /><path d="M12 9v4M12 17h.01" /></>} />
);
const ImageIcon = (p) => (
  <Icon {...p} d={<><rect x="3" y="3" width="18" height="18" rx="2" /><circle cx="8.5" cy="8.5" r="1.5" /><path d="m21 15-5-5L5 21" /></>} />
);
const PenToolIcon = (p) => (
  <Icon {...p} d={<><path d="m12 19 7-7 3 3-7 7-3-3z" /><path d="m18 13-1.5-7.5L2 2l3.5 14.5L13 18l5-5z" /><path d="m2 2 7.586 7.586" /><circle cx="11" cy="11" r="2" /></>} />
);
const ArrowLeftIcon = (p) => (
  <Icon {...p} d={<><path d="m12 19-7-7 7-7" /><path d="M19 12H5" /></>} />
);
const SaveIcon = (p) => (
  <Icon {...p} d={<><path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z" /><polyline points="17 21 17 13 7 13 7 21" /><polyline points="7 3 7 8 15 8" /></>} />
);

/* ────────── Toast Notifications ────────── */
const SuccessToast = ({ message, onClose }) => (
  <div className="dc-toast dc-toast--success" role="alert">
    <span className="dc-toast__icon"><CheckCircleIcon size={20} /></span>
    <span className="dc-toast__msg">{message}</span>
    <button className="dc-toast__close" onClick={onClose} type="button" aria-label="Close">
      <CloseIcon size={15} />
    </button>
  </div>
);

const ErrorToast = ({ message, onClose }) => (
  <div className="dc-toast dc-toast--error" role="alert">
    <span className="dc-toast__icon"><XCircleIcon size={20} /></span>
    <span className="dc-toast__msg">{message}</span>
    <button className="dc-toast__close" onClick={onClose} type="button" aria-label="Close">
      <CloseIcon size={15} />
    </button>
  </div>
);

/* ────────── Confirm Dialog (replaces window.confirm) ────────── */
const ConfirmDialog = ({ message, onConfirm, onCancel }) => (
  <div className="dc-modal-overlay" onClick={(e) => { if (e.target === e.currentTarget) onCancel(); }}>
    <div className="dc-confirm-modal">
      <div className="dc-confirm__icon-wrap">
        <AlertTriangleIcon size={28} />
      </div>
      <h3 className="dc-confirm__title">Are you sure?</h3>
      <p className="dc-confirm__msg">{message}</p>
      <div className="dc-confirm__actions">
        <button className="dc-btn-cancel" type="button" onClick={onCancel}>Cancel</button>
        <button className="dc-btn-danger" type="button" onClick={onConfirm}>Yes, Proceed</button>
      </div>
    </div>
  </div>
);

/* ────────── Custom react-select styles ────────── */
const buildSelectStyles = (hasError = false) => ({
  control: (base, state) => ({
    ...base,
    borderRadius: "10px",
    border: `1px solid ${hasError ? "#e0413c" : state.isFocused ? "#10b981" : "#e3f0e9"}`,
    boxShadow: state.isFocused
      ? hasError
        ? "0 0 0 4px rgba(224,65,60,.12)"
        : "0 0 0 4px rgba(16,185,129,.12)"
      : "none",
    background: "#f9fcfb",
    minHeight: "46px",
    fontSize: "14px",
    cursor: "pointer",
    "&:hover": { borderColor: "#10b981" },
    transition: "border-color .15s, box-shadow .15s, background .15s",
  }),
  menu: (base) => ({
    ...base,
    borderRadius: "12px",
    border: "1px solid #e3f0e9",
    boxShadow: "0 12px 32px rgba(16,30,24,.12)",
    overflow: "hidden",
    zIndex: 9999,
  }),
  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
  option: (base, state) => ({
    ...base,
    background: state.isSelected ? "#10b981" : state.isFocused ? "#ecfdf5" : "#fff",
    color: state.isSelected ? "#fff" : "#0f1f1a",
    fontSize: "14px",
    padding: "10px 14px",
    cursor: "pointer",
  }),
  multiValue: (base) => ({
    ...base,
    background: "#ecfdf5",
    border: "1px solid #b7e8d2",
    borderRadius: "999px",
    padding: "0 2px",
  }),
  multiValueLabel: (base) => ({
    ...base,
    color: "#047857",
    fontSize: "12px",
    fontWeight: 600,
    paddingLeft: "8px",
  }),
  multiValueRemove: (base) => ({
    ...base,
    color: "#047857",
    borderRadius: "0 999px 999px 0",
    "&:hover": { background: "#d1fae5", color: "#065f46" },
  }),
  placeholder: (base) => ({ ...base, color: "#9aa8a2", fontSize: "14px" }),
  singleValue: (base) => ({ ...base, color: "#0f1f1a" }),
});

/* ────────── Section Card ────────── */
const SectionCard = ({ title, icon, children }) => (
  <div className="dc-section-card">
    <div className="dc-section-header">
      <div className="dc-section-icon">{icon}</div>
      <span className="dc-section-title">{title}</span>
    </div>
    <div className="dc-section-body">{children}</div>
  </div>
);

/* ────────── Form Field ────────── */
const Field = ({ label, required, error, children, half }) => (
  <div className={`dc-field${half ? " dc-field--half" : ""}`}>
    <label className="dc-label">
      {label} {required && <span className="dc-label__req">*</span>}
    </label>
    {children}
    {error && <div className="dc-field__error">{error}</div>}
  </div>
);

/* ────────── Main Component ────────── */
const DoctorAdd = () => {
  const navigate = useNavigate();
  const token = localStorage.getItem("authToken");

  const [loading, setLoading] = useState(false);
  const [images, setImages] = useState([]);
  const [errors, setErrors] = useState({});
  const [toast, setToast] = useState({ show: false, type: "", message: "" });
  const [confirmDialog, setConfirmDialog] = useState({ show: false, message: "", onConfirm: null });

  const [departments, setDepartments] = useState([]);
  const [specializations, setSpecializations] = useState([]);
  const [clinics, setClinics] = useState([]);
  const [treatments, setTreatments] = useState([]);
  const [signature, setSignature] = useState(null);
  const [signaturePreview, setSignaturePreview] = useState(null);
  const [imagePreviews, setImagePreviews] = useState([]);

  const [doctor, setDoctor] = useState({
    name: "",
    department: "",
    specialization: [],
    clinic: [],
    gender: "",
    experience: "",
    status: "Active",
    short_desc: "",
    description: "",
    consult_price: "",
    videocall_price: "",
    timeslot: "15",
    phone: "",
    address: "",
    city: "",
    pincode: "",
    treatment: [],
  });

  const flashToast = (message, type = "success") => {
    setToast({ show: true, type, message });
    setTimeout(() => setToast({ show: false, type: "", message: "" }), 4500);
  };

  useEffect(() => {
    fetchDepartments();
    fetchSpecializations();
    fetchClinics();
    fetchTreatments();
  }, []);

  const fetchDepartments = async () => {
    try {
      const res = await fetch(`${API_BASE_URL}api/department`, { headers: { Authorization: `Bearer ${token}` } });
      const data = await res.json();
      if (data.status) setDepartments(data.data.map((d) => ({ value: d.id, label: d.name })));
    } catch {}
  };

  const fetchTreatments = async () => {
    try {
      const res = await fetch(`${API_BASE_URL}api/treatments_front`, { headers: { Authorization: `Bearer ${token}` } });
      const data = await res.json();
      if (data.status) setTreatments(data.data.map((d) => ({ value: d.id, label: d.name })));
    } catch {}
  };

  const fetchSpecializations = async () => {
    try {
      const res = await fetch(`${API_BASE_URL}api/specialization`, { headers: { Authorization: `Bearer ${token}` } });
      const data = await res.json();
      if (data.status) setSpecializations(data.data.map((d) => ({ value: d.id, label: d.name })));
    } catch {}
  };

  const fetchClinics = async () => {
    try {
      const res = await fetch(`${API_BASE_URL}api/clinic_front`, { headers: { Authorization: `Bearer ${token}` } });
      const data = await res.json();
      if (data.status) setClinics(data.data.map((d) => ({ value: d.id, label: d.name })));
    } catch {}
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setDoctor((prev) => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    setImages(files);
    const previews = files.map((f) => URL.createObjectURL(f));
    setImagePreviews(previews);
  };

  useEffect(() => {
    if (!signature) { setSignaturePreview(null); return; }
    const url = URL.createObjectURL(signature);
    setSignaturePreview(url);
    return () => URL.revokeObjectURL(url);
  }, [signature]);

  const validateForm = () => {
    let newErrors = {};
    if (!doctor.name) newErrors.name = "Doctor name is required";
    if (!doctor.consult_price) newErrors.consult_price = "Consult price is required";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const showConfirm = (message) =>
    new Promise((resolve) => {
      setConfirmDialog({
        show: true,
        message,
        onConfirm: () => { setConfirmDialog({ show: false, message: "", onConfirm: null }); resolve(true); },
      });
    });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    const confirmed = await showConfirm("Are you sure you want to create this doctor profile?");
    if (!confirmed) return;

    setLoading(true);

    const formData = new FormData();
    formData.append("name", doctor.name);
    formData.append("department", doctor.department);
    formData.append("gender", doctor.gender);
    formData.append("experience", doctor.experience);
    formData.append("status", doctor.status);
    formData.append("short_desc", doctor.short_desc);
    formData.append("description", doctor.description);
    formData.append("consult_price", doctor.consult_price);
    formData.append("videocall_price", doctor.videocall_price);
    formData.append("timeslot", doctor.timeslot);
    formData.append("phone", doctor.phone);
    formData.append("address", doctor.address);
    formData.append("city", doctor.city);
    formData.append("pincode", doctor.pincode);
    formData.append("specialization", doctor.specialization.length ? doctor.specialization.map((s) => s.value).join(",") : "");
    formData.append("clinic", doctor.clinic.length ? doctor.clinic.map((c) => c.value).join(",") : "");
    formData.append("treatment", doctor.treatment.length ? doctor.treatment.map((s) => s.value).join("|") : "");
    images.forEach((file, i) => formData.append(`images[${i}]`, file));
    if (signature) formData.append("signature", signature);

    try {
      const response = await fetch(`${API_BASE_URL}api/doctors`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });
      const data = await response.json();
      if (data.status) {
        flashToast("Doctor profile created successfully!", "success");
        setTimeout(() => navigate("/doctor"), 1800);
      } else {
        flashToast("Failed to create doctor profile.", "error");
      }
    } catch (error) {
      console.error(error);
      flashToast("Something went wrong. Please try again.", "error");
    } finally {
      setLoading(false);
    }
  };

  const selectStyles = buildSelectStyles();

  return (
    <>
      <style>{`
        :root {
          --bc-bg:           #f3faf6;
          --bc-card:         #ffffff;
          --bc-border:       #e3f0e9;
          --bc-text:         #0f1f1a;
          --bc-muted:        #6b7a74;
          --bc-primary:      #10b981;
          --bc-primary-d:    #059669;
          --bc-primary-grad: linear-gradient(135deg, #14b8a6 0%, #10b981 100%);
          --bc-soft:         #ecfdf5;
          --bc-edit:         #fff1e6;
          --bc-edit-fg:      #ea7a3b;
          --bc-del:          #fee4e2;
          --bc-del-fg:       #e0413c;
          --bc-shadow:       0 1px 2px rgba(16,30,24,.04), 0 4px 14px rgba(16,30,24,.04);
          --bc-shadow-lg:    0 8px 32px rgba(16,30,24,.1);
        }

        /* ── Shell ── */
        .dc-shell { background: var(--bc-bg); min-height: 100%; padding: 24px; }

        /* ── Page Header ── */
        .dc-head {
          display: flex; align-items: flex-start;
          justify-content: space-between; gap: 16px;
          flex-wrap: wrap; margin-bottom: 24px;
        }
        .dc-head-left { display: flex; align-items: center; gap: 14px; }
        .dc-head-icon {
          width: 48px; height: 48px;
          background: var(--gradient-brand); border-radius: 14px;
          display: flex; align-items: center; justify-content: center;
          box-shadow: 0 6px 18px rgba(16,185,129,.28); flex-shrink: 0;
        }
        .dc-head-icon svg { fill: none !important; color: #fff; }
        .dc-head h2 {
          font-size: clamp(20px, 2.2vw, 28px); font-weight: 800;
          color: var(--bc-text); margin: 0 0 3px 0; letter-spacing: -0.01em;
        }
        .dc-head p { color: var(--bc-muted); font-size: 14px; margin: 0; }
        .dc-back-btn {
          display: inline-flex; align-items: center; gap: 8px;
          background: #fff; color: var(--bc-muted);
          border: 1px solid var(--color-border); padding: 10px 16px;
          font-weight: 500; font-size: 14px; border-radius: 10px;
          cursor: pointer; transition: all .15s ease; white-space: nowrap;
          text-decoration: none;
        }
        .dc-back-btn svg { fill: none !important; }
        .dc-back-btn:hover { background: #f6fbf8; color: var(--bc-text); border-color: var(--bc-primary-grad); }

        /* ── Toast ── */
        .dc-toast {
          display: flex; align-items: center; gap: 12px;
          padding: 14px 18px; border-radius: 14px;
          margin: 0 0 20px 0; font-size: 14px; font-weight: 500;
          animation: dcDropIn .3s cubic-bezier(.34,1.56,.64,1);
          box-shadow: 0 4px 20px rgba(0,0,0,.09);
          position: sticky; top: 12px; z-index: 100;
        }
        .dc-toast--success { background: #ecfdf5; color: #065f46; border: 1px solid #b7e8d2; }
        .dc-toast--error   { background: #fff1f0; color: #991b1b; border: 1px solid #fbd7d3; }
        .dc-toast__icon svg { fill: none !important; }
        .dc-toast__msg { flex: 1; }
        .dc-toast__close {
          display: inline-flex; align-items: center; justify-content: center;
          background: transparent; border: none; cursor: pointer;
          color: inherit; opacity: .65; padding: 4px; border-radius: 6px;
          transition: opacity .15s;
        }
        .dc-toast__close:hover { opacity: 1; }
        .dc-toast__close svg { fill: none !important; }

        /* ── Section Card ── */
        .dc-section-card {
          background: var(--bc-card); border: 1px solid var(--color-border);
          border-radius: 18px; box-shadow: var(--bc-shadow);
          overflow: visible; margin-bottom: 20px;
        }
        .dc-section-header {
          display: flex; align-items: center; gap: 12px;
          padding: 18px 22px; border-bottom: 1px solid #f0f6f3;
          background: linear-gradient(135deg, #ecfdf5 0%, #f0fbf6 100%);
          border-radius: 18px 18px 0 0;
        }
        .dc-section-icon {
          width: 36px; height: 36px;
          background: var(--gradient-brand); border-radius: 9px;
          display: flex; align-items: center; justify-content: center;
          box-shadow: 0 4px 10px rgba(16,185,129,.25); flex-shrink: 0;
        }
        .dc-section-icon svg { fill: none !important; color: #fff; }
        .dc-section-title { font-size: 15px; font-weight: 700; color: var(--bc-text); }
        .dc-section-body { padding: 22px; }

        /* ── Grid Layout ── */
        .dc-grid {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 18px;
        }
        .dc-grid .dc-field--full { grid-column: 1 / -1; }

        /* ── Field ── */
        .dc-field { display: flex; flex-direction: column; gap: 6px; position: relative; }
        .dc-field--half { /* normal, half width already from grid */ }
        .dc-label {
          font-size: 13px; font-weight: 600; color: #3d4a45;
        }
        .dc-label__req { color: var(--bc-del-fg); margin-left: 2px; }
        .dc-field__error {
          font-size: 12px; color: var(--bc-del-fg); font-weight: 500;
          display: flex; align-items: center; gap: 4px;
        }

        /* ── Form Controls ── */
        .dc-input, .dc-select, .dc-textarea {
          width: 100%; box-sizing: border-box;
          padding: 11px 14px; border-radius: 10px;
          border: 1px solid var(--color-border); background: #f9fcfb;
          font-size: 14px; color: var(--bc-text); outline: none;
          transition: border-color .15s, box-shadow .15s, background .15s;
          font-family: inherit;
        }
        .dc-input::placeholder, .dc-textarea::placeholder { color: #9aa8a2; }
        .dc-input:focus, .dc-select:focus, .dc-textarea:focus {
          border-color: var(--bc-primary-grad); background: #fff;
          box-shadow: 0 0 0 4px rgba(16,185,129,.12);
        }
        .dc-input--error, .dc-select--error {
          border-color: var(--bc-del-fg) !important;
          box-shadow: 0 0 0 4px rgba(224,65,60,.08) !important;
        }
        .dc-select {
          appearance: none;
          background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='%239aa8a2' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='m6 9 6 6 6-6'/%3E%3C/svg%3E");
          background-repeat: no-repeat;
          background-position: right 12px center;
          padding-right: 38px;
          cursor: pointer;
        }
        .dc-textarea { resize: vertical; min-height: 90px; }

        /* ── Image Upload ── */
        .dc-upload-area {
          border: 2px dashed var(--color-border); border-radius: 12px;
          padding: 22px; text-align: center; cursor: pointer;
          background: #f9fcfb; transition: all .2s ease;
          position: relative; overflow: hidden;
        }
        .dc-upload-area:hover { border-color: var(--bc-primary-grad); background: var(--bc-soft); }
        .dc-upload-area input[type="file"] {
          position: absolute; inset: 0; opacity: 0; cursor: pointer;
          width: 100%; height: 100%;
        }
        .dc-upload-icon { color: #b7e8d2; margin-bottom: 8px; }
        .dc-upload-icon svg { fill: none !important; }
        .dc-upload-label { font-size: 13px; color: var(--bc-muted); }
        .dc-upload-label strong { color: var(--bc-primary-grad); }
        .dc-upload-hint { font-size: 11px; color: #b0bcb6; margin-top: 2px; display: block; }

        /* ── Image Previews Grid ── */
        .dc-img-preview-grid {
          display: flex; flex-wrap: wrap; gap: 10px; margin-top: 14px;
        }
        .dc-img-preview-item {
          width: 72px; height: 72px; border-radius: 10px; overflow: hidden;
          border: 2px solid #b7e8d2; box-shadow: 0 2px 8px rgba(16,30,24,.08);
          flex-shrink: 0;
        }
        .dc-img-preview-item img { width: 100%; height: 100%; object-fit: cover; display: block; }

        /* ── Signature Preview ── */
        .dc-sig-preview {
          display: flex; align-items: center; gap: 14px;
          margin-top: 12px; padding: 12px;
          background: var(--bc-soft); border-radius: 10px;
          border: 1px solid #b7e8d2;
        }
        .dc-sig-preview img {
          height: 50px; max-width: 140px; object-fit: contain;
          border-radius: 6px; border: 1px solid #e3f0e9; background: #fff; padding: 4px;
        }
        .dc-sig-preview-info { flex: 1; }
        .dc-sig-preview-name { font-size: 13px; font-weight: 600; color: var(--bc-text); }
        .dc-sig-preview-hint { font-size: 12px; color: var(--bc-muted); margin-top: 2px; }
        .dc-sig-remove {
          background: var(--bc-del); color: var(--bc-del-fg);
          border: none; border-radius: 7px; cursor: pointer;
          display: inline-flex; align-items: center; justify-content: center;
          width: 28px; height: 28px; transition: filter .15s; flex-shrink: 0;
        }
        .dc-sig-remove:hover { filter: brightness(.93); }
        .dc-sig-remove svg { fill: none !important; }

        /* ── Submit bar ── */
        .dc-submit-bar {
          display: flex; align-items: center; justify-content: flex-end;
          gap: 12px; padding: 20px 0 4px;
        }
        .dc-btn-cancel {
          padding: 12px 22px; border-radius: 10px;
          background: #fff; border: 1px solid var(--color-border);
          font-size: 14px; font-weight: 500; color: var(--bc-muted);
          cursor: pointer; transition: all .15s;
        }
        .dc-btn-cancel:hover { background: #f6fbf8; color: var(--bc-text); }
        .dc-btn-submit {
          display: inline-flex; align-items: center; gap: 8px;
          padding: 12px 26px; border-radius: 12px;
          background: var(--gradient-brand); color: #fff;
          border: none; font-size: 14px; font-weight: 700; cursor: pointer;
          box-shadow: 0 6px 18px rgba(16,185,129,.25);
          transition: filter .15s, box-shadow .15s, transform .15s;
        }
        .dc-btn-submit svg { fill: none !important; }
        .dc-btn-submit:hover { filter: brightness(1.05); box-shadow: 0 10px 24px rgba(16,185,129,.3); transform: translateY(-1px); }
        .dc-btn-submit:active { transform: translateY(0); }
        .dc-btn-submit:disabled { opacity: .6; cursor: not-allowed; transform: none; }

        /* ── Loading overlay ── */
        .dc-overlay {
          position: fixed; inset: 0; z-index: 1080;
          background: rgba(255,255,255,.55); backdrop-filter: blur(2px);
          display: grid; place-items: center;
        }
        .dc-spinner {
          width: 42px; height: 42px; border-radius: 50%;
          border: 3px solid #d6efe2; border-top-color: var(--bc-primary-grad);
          animation: dcSpin 0.8s linear infinite;
        }

        /* ── Confirm Modal ── */
        .dc-modal-overlay {
          position: fixed; inset: 0; z-index: 1050;
          background: rgba(10,20,15,.45); backdrop-filter: blur(4px);
          display: flex; align-items: center; justify-content: center;
          padding: 16px; animation: dcFadeIn .2s ease;
        }
        .dc-confirm-modal {
          background: #fff; border-radius: 20px;
          box-shadow: 0 30px 60px rgba(16,30,24,.2), 0 0 0 1px rgba(16,185,129,.08);
          width: 100%; max-width: 400px; overflow: hidden; text-align: center;
          padding: 36px 28px 28px;
          animation: dcSlideUp .25s cubic-bezier(0.34,1.56,0.64,1);
        }
        .dc-confirm__icon-wrap {
          width: 60px; height: 60px; border-radius: 50%;
          background: #fff7ed; border: 2px solid #fdba74;
          display: flex; align-items: center; justify-content: center;
          margin: 0 auto 16px; color: #ea7a3b;
        }
        .dc-confirm__icon-wrap svg { fill: none !important; }
        .dc-confirm__title { font-size: 18px; font-weight: 800; color: var(--bc-text); margin: 0 0 8px; }
        .dc-confirm__msg { font-size: 14px; color: var(--bc-muted); margin: 0 0 24px; line-height: 1.5; }
        .dc-confirm__actions { display: flex; gap: 10px; justify-content: center; }
        .dc-btn-danger {
          display: inline-flex; align-items: center; gap: 8px;
          padding: 10px 22px; border-radius: 10px;
          background: var(--bc-del); color: var(--bc-del-fg);
          border: 1px solid #fbcfcd; font-size: 14px; font-weight: 600;
          cursor: pointer; transition: all .15s;
        }
        .dc-btn-danger:hover { background: #fdd1cf; }

        /* ── Animations ── */
        @keyframes dcDropIn { from { opacity: 0; transform: translateY(-6px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes dcFadeIn { from { opacity: 0; } to { opacity: 1; } }
        @keyframes dcSlideUp { from { opacity: 0; transform: translateY(28px) scale(0.97); } to { opacity: 1; transform: translateY(0) scale(1); } }
        @keyframes dcSpin { to { transform: rotate(360deg); } }

        /* ── Rich editor tweaks ── */
        .dc-section-body .sun-editor { border-radius: 10px !important; border-color: var(--color-border) !important; }
        .dc-section-body .sun-editor .se-toolbar { border-radius: 10px 10px 0 0 !important; background: #f3faf6 !important; border-color: var(--color-border) !important; }
        .dc-section-body .sun-editor .se-container { border-radius: 0 0 10px 10px !important; }

        /* ── Responsive ── */
        @media (max-width: 768px) {
          .dc-shell { padding: 14px; }
          .dc-head { flex-direction: column; align-items: stretch; }
          .dc-grid { grid-template-columns: 1fr; }
          .dc-grid .dc-field--full { grid-column: 1 / -1; }
          .dc-submit-bar { flex-direction: column-reverse; }
          .dc-btn-submit, .dc-btn-cancel { width: 100%; justify-content: center; }
        }

        .dc-shell svg { fill: none; }
      `}</style>

      <div id="layout-wrapper">
        <Header />

        {loading && (
          <div className="dc-overlay">
            <div className="dc-spinner" />
          </div>
        )}

        {confirmDialog.show && (
          <ConfirmDialog
            message={confirmDialog.message}
            onConfirm={confirmDialog.onConfirm}
            onCancel={() => setConfirmDialog({ show: false, message: "", onConfirm: null })}
          />
        )}

        <div className="main-content">
          <div className="page-content">
            <div className="dc-shell">

              {/* ── Page Header ── */}
              <div className="dc-head">
                <div className="dc-head-left">
                  <div className="dc-head-icon">
                    <UserIcon size={22} />
                  </div>
                  <div>
                    <h2>Create Doctor</h2>
                    <p>Add a new doctor profile to your system.</p>
                  </div>
                </div>
                <button type="button" className="dc-back-btn" onClick={() => navigate("/doctor")}>
                  <ArrowLeftIcon size={16} />
                  Back to Doctors
                </button>
              </div>

              {/* ── Toast ── */}
              {toast.show && toast.type === "success" && (
                <SuccessToast message={toast.message} onClose={() => setToast({ show: false, type: "", message: "" })} />
              )}
              {toast.show && toast.type === "error" && (
                <ErrorToast message={toast.message} onClose={() => setToast({ show: false, type: "", message: "" })} />
              )}

              <form onSubmit={handleSubmit}>

                {/* ── Section 1: Basic Info ── */}
                <SectionCard
                  title="Basic Information"
                  icon={<UserIcon size={17} />}
                >
                  <div className="dc-grid">

                    <div className="dc-field">
                      <label className="dc-label">Doctor Name <span className="dc-label__req">*</span></label>
                      <input
                        name="name"
                        className={`dc-input${errors.name ? " dc-input--error" : ""}`}
                        placeholder="Dr. Full Name"
                        value={doctor.name}
                        onChange={handleChange}
                      />
                      {errors.name && <div className="dc-field__error">{errors.name}</div>}
                    </div>

                    <div className="dc-field">
                      <label className="dc-label">Department</label>
                      <Select
                        options={departments}
                        value={departments.find((o) => o.value === doctor.department) || null}
                        onChange={(selected) => setDoctor((prev) => ({ ...prev, department: selected ? selected.value : "" }))}
                        styles={selectStyles}
                        menuPortalTarget={document.body}
                        placeholder="Select department…"
                        isClearable
                      />
                    </div>

                    <div className="dc-field">
                      <label className="dc-label">Gender</label>
                      <select
                        name="gender"
                        className="dc-select"
                        value={doctor.gender}
                        onChange={handleChange}
                      >
                        <option value="">Select Gender</option>
                        <option value="Male">Male</option>
                        <option value="Female">Female</option>
                        <option value="Other">Other</option>
                      </select>
                    </div>

                    <div className="dc-field">
                      <label className="dc-label">Experience</label>
                      <input
                        name="experience"
                        className="dc-input"
                        placeholder="e.g. 10 years"
                        value={doctor.experience}
                        onChange={handleChange}
                      />
                    </div>

                    <div className="dc-field">
                      <label className="dc-label">Specialization</label>
                      <Select
                        options={specializations}
                        isMulti
                        isSearchable
                        value={doctor.specialization}
                        onChange={(selected) => setDoctor((prev) => ({ ...prev, specialization: selected || [] }))}
                        styles={selectStyles}
                        menuPortalTarget={document.body}
                        placeholder="Select specializations…"
                      />
                    </div>

                    <div className="dc-field">
                      <label className="dc-label">Clinic</label>
                      <Select
                        options={clinics}
                        isMulti
                        isSearchable
                        value={doctor.clinic}
                        onChange={(selected) => setDoctor((prev) => ({ ...prev, clinic: selected || [] }))}
                        styles={selectStyles}
                        menuPortalTarget={document.body}
                        placeholder="Select clinics…"
                      />
                    </div>

                    <div className="dc-field">
                      <label className="dc-label">Treatment</label>
                      <Select
                        options={treatments}
                        isMulti
                        value={doctor.treatment}
                        onChange={(selected) => setDoctor((prev) => ({ ...prev, treatment: selected || [] }))}
                        styles={selectStyles}
                        menuPortalTarget={document.body}
                        placeholder="Select treatments…"
                      />
                    </div>

                    <div className="dc-field">
                      <label className="dc-label">Phone</label>
                      <input
                        name="phone"
                        type="text"
                        className="dc-input"
                        placeholder="+91 00000 00000"
                        value={doctor.phone}
                        onChange={handleChange}
                      />
                    </div>

                  </div>
                </SectionCard>

                {/* ── Section 2: Pricing & Schedule ── */}
                <SectionCard
                  title="Pricing & Schedule"
                  icon={<SaveIcon size={17} />}
                >
                  <div className="dc-grid">

                    <div className="dc-field">
                      <label className="dc-label">In-Patient Price <span className="dc-label__req">*</span></label>
                      <input
                        name="consult_price"
                        type="number"
                        min={0}
                        className={`dc-input${errors.consult_price ? " dc-input--error" : ""}`}
                        placeholder="₹ 0.00"
                        value={doctor.consult_price}
                        onChange={handleChange}
                      />
                      {errors.consult_price && <div className="dc-field__error">{errors.consult_price}</div>}
                    </div>

                    <div className="dc-field">
                      <label className="dc-label">Video Call Price</label>
                      <input
                        name="videocall_price"
                        type="number"
                        min={0}
                        className="dc-input"
                        placeholder="₹ 0.00"
                        value={doctor.videocall_price}
                        onChange={handleChange}
                      />
                    </div>

                    <div className="dc-field">
                      <label className="dc-label">Appointment Slot Duration</label>
                      <select
                        name="timeslot"
                        className="dc-select"
                        value={doctor.timeslot}
                        onChange={handleChange}
                      >
                        <option value="">Select Slot</option>
                        <option value="10">10 Minutes</option>
                        <option value="15">15 Minutes</option>
                        <option value="20">20 Minutes</option>
                        <option value="30">30 Minutes</option>
                      </select>
                    </div>

                    <div className="dc-field">
                      <label className="dc-label">Status</label>
                      <select
                        name="status"
                        className="dc-select"
                        value={doctor.status}
                        onChange={handleChange}
                      >
                        <option value="Active">Active</option>
                        <option value="InActive">Inactive</option>
                      </select>
                    </div>

                  </div>
                </SectionCard>

                {/* ── Section 3: Location ── */}
                <SectionCard
                  title="Location Details"
                  icon={<ChevronLeftIcon size={17} />}
                >
                  <div className="dc-grid">

                    <div className="dc-field" style={{ gridColumn: "1 / -1" }}>
                      <label className="dc-label">Address</label>
                      <input
                        name="address"
                        type="text"
                        className="dc-input"
                        placeholder="Street address"
                        value={doctor.address}
                        onChange={handleChange}
                      />
                    </div>

                    <div className="dc-field">
                      <label className="dc-label">City</label>
                      <input
                        name="city"
                        type="text"
                        className="dc-input"
                        placeholder="City"
                        value={doctor.city}
                        onChange={handleChange}
                      />
                    </div>

                    <div className="dc-field">
                      <label className="dc-label">Pincode</label>
                      <input
                        name="pincode"
                        type="text"
                        className="dc-input"
                        placeholder="000000"
                        value={doctor.pincode}
                        onChange={handleChange}
                      />
                    </div>

                    <div className="dc-field" style={{ gridColumn: "1 / -1" }}>
                      <label className="dc-label">Short Description</label>
                      <textarea
                        name="short_desc"
                        className="dc-textarea"
                        rows="3"
                        placeholder="Brief introduction or bio…"
                        value={doctor.short_desc}
                        onChange={handleChange}
                      />
                    </div>

                  </div>
                </SectionCard>

                {/* ── Section 4: Description ── */}
                <SectionCard
                  title="Full Description"
                  icon={<PenToolIcon size={17} />}
                >
                  <SunEditor
                    name="description"
                    setContents={doctor.description}
                    onChange={(content) => setDoctor((prev) => ({ ...prev, description: content }))}
                    setOptions={{ height: 300 }}
                  />
                </SectionCard>

                {/* ── Section 5: Images ── */}
                <SectionCard
                  title="Doctor Images"
                  icon={<ImageIcon size={17} />}
                >
                  <div className="dc-upload-area">
                    <input
                      type="file"
                      multiple
                      accept="image/*"
                      onChange={handleFileChange}
                    />
                    <div className="dc-upload-icon"><ImageIcon size={34} /></div>
                    <div className="dc-upload-label">
                      <strong>Click to upload</strong> or drag & drop
                      <span className="dc-upload-hint">PNG, JPG, WEBP up to 5MB each</span>
                    </div>
                  </div>
                  {imagePreviews.length > 0 && (
                    <div className="dc-img-preview-grid">
                      {imagePreviews.map((src, i) => (
                        <div className="dc-img-preview-item" key={i}>
                          <img src={src} alt={`Preview ${i + 1}`} />
                        </div>
                      ))}
                    </div>
                  )}
                </SectionCard>

                {/* ── Section 6: Signature ── */}
                <SectionCard
                  title="Digital Signature"
                  icon={<PenToolIcon size={17} />}
                >
                  {!signaturePreview ? (
                    <div className="dc-upload-area">
                      <input
                        type="file"
                        accept="image/png, image/jpeg"
                        onChange={(e) => setSignature(e.target.files[0])}
                      />
                      <div className="dc-upload-icon"><PenToolIcon size={34} /></div>
                      <div className="dc-upload-label">
                        <strong>Click to upload</strong> signature
                        <span className="dc-upload-hint">PNG/JPG with transparent background recommended</span>
                      </div>
                    </div>
                  ) : (
                    <div className="dc-sig-preview">
                      <img src={signaturePreview} alt="Signature preview" />
                      <div className="dc-sig-preview-info">
                        <div className="dc-sig-preview-name">{signature?.name || "Signature"}</div>
                        <div className="dc-sig-preview-hint">
                          {signature ? `${(signature.size / 1024).toFixed(1)} KB` : ""}
                        </div>
                      </div>
                      <button
                        type="button"
                        className="dc-sig-remove"
                        onClick={() => { setSignature(null); setSignaturePreview(null); }}
                        title="Remove signature"
                      >
                        <CloseIcon size={14} />
                      </button>
                    </div>
                  )}
                </SectionCard>

                {/* ── Submit Bar ── */}
                <div className="dc-submit-bar">
                  <button type="button" className="dc-btn-cancel" onClick={() => navigate("/doctor")}>
                    Cancel
                  </button>
                  <button type="submit" className="dc-btn-submit" disabled={loading}>
                    <SaveIcon size={16} />
                    {loading ? "Creating…" : "Create Doctor"}
                  </button>
                </div>

              </form>

            </div>
          </div>

          <Footer />
        </div>
      </div>
    </>
  );
};

export default DoctorAdd;